import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  MapPin,
  Globe,
} from 'lucide-react';
import api from '../../utils/api';
import {
  getVerificationToken,
  hasVerifiedRegistration,
} from '../../utils/registrationSession';
import { formatRegistrationText } from '../../utils/textFormat';
import { INDIAN_STATES, BOARD_OPTIONS } from '../../data/indianStates';
import { SCHOOL_TYPE_OPTIONS, ACADEMIC_YEAR_OPTIONS } from '../../data/schoolRegistrationOptions';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import RegisterLayout from './RegisterLayout';

const schema = z.object({
  school_name: z.string().min(2, 'School name is required'),
  school_type: z.string().min(1, 'School type is required'),
  board: z.string().min(1, 'Board is required'),
  academic_year: z.string().min(1, 'Academic year is required'),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  pincode: z.string().optional(),
});

function previewSchoolCode(schoolName) {
  const base = (schoolName || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return (base || 'SCH').slice(0, 12);
}

export default function RegisterSchoolStep() {
  const navigate = useNavigate();
  const [serverError, setServerError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      school_name: '',
      school_type: '',
      board: '',
      academic_year: ACADEMIC_YEAR_OPTIONS[0]?.value || '',
      address: '',
      city: '',
      state: '',
      country: 'India',
      pincode: '',
    },
  });

  const schoolName = watch('school_name');
  const codePreview = useMemo(() => previewSchoolCode(schoolName), [schoolName]);

  useEffect(() => {
    if (!hasVerifiedRegistration()) {
      navigate('/register', { replace: true });
    }
  }, [navigate]);

  const onSubmit = async (values) => {
    setServerError('');

    const token = getVerificationToken();
    if (!token) {
      navigate('/register', { replace: true });
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await api.post('auth/register/complete', {
        verification_token: token,
        school_name: formatRegistrationText(values.school_name),
        school_code: codePreview || undefined,
        school_type: values.school_type,
        board: values.board,
        academic_year: values.academic_year,
        address: values.address ? formatRegistrationText(values.address) : undefined,
        city: values.city ? formatRegistrationText(values.city) : undefined,
        state: values.state || undefined,
        country: values.country ? formatRegistrationText(values.country) : 'India',
        pincode: values.pincode?.trim() || undefined,
      });

      const result = data?.data || {};
      navigate('/register/success', { state: { registration: result } });
    } catch (e) {
      setServerError(e?.response?.data?.message || e?.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  const firstError =
    serverError
    || Object.values(errors).find((e) => e?.message)?.message;

  return (
    <RegisterLayout
      step={3}
      title="School details"
      description="Step 3 — Set up your school"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
        {firstError && (
          <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {firstError}
          </div>
        )}

        <p className="text-sm font-medium text-foreground flex items-center gap-2">
          <Building2 className="w-4 h-4" /> School information
        </p>

        <div className="space-y-2">
          <Label htmlFor="school_name">School Name</Label>
          <Input id="school_name" placeholder="Springfield High School" {...register('school_name')} />
        </div>

        <div className="space-y-2">
          <Label>School Code</Label>
          <Input value={codePreview} readOnly className="bg-muted/50 font-mono" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>School Type</Label>
            <Select onValueChange={(v) => setValue('school_type', v, { shouldValidate: true })}>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {SCHOOL_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Board</Label>
            <Select onValueChange={(v) => setValue('board', v, { shouldValidate: true })}>
              <SelectTrigger>
                <SelectValue placeholder="Select board" />
              </SelectTrigger>
              <SelectContent>
                {BOARD_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Academic Year</Label>
          <Select
            defaultValue={ACADEMIC_YEAR_OPTIONS[0]?.value}
            onValueChange={(v) => setValue('academic_year', v, { shouldValidate: true })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select year" />
            </SelectTrigger>
            <SelectContent>
              {ACADEMIC_YEAR_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="address">Address</Label>
          <div className="relative">
            <MapPin className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
            <Input id="address" className="pl-10" placeholder="Street address" {...register('address')} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="city">City</Label>
            <Input id="city" placeholder="City" {...register('city')} />
          </div>
          <div className="space-y-2">
            <Label>State</Label>
            <Select onValueChange={(v) => setValue('state', v)}>
              <SelectTrigger>
                <SelectValue placeholder="State" />
              </SelectTrigger>
              <SelectContent>
                {INDIAN_STATES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="country">Country</Label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input id="country" className="pl-10" {...register('country')} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="pincode">Postal Code</Label>
            <Input id="pincode" placeholder="PIN" {...register('pincode')} />
          </div>
        </div>

        <div className="flex gap-2 pt-2 sticky bottom-0 bg-card/95 pb-1">
          <Button type="button" variant="outline" className="flex-1" onClick={() => navigate('/register/verify')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button type="submit" className="flex-1" disabled={submitting}>
            {submitting ? 'Creating school…' : 'Create School'}
          </Button>
        </div>
      </form>
    </RegisterLayout>
  );
}
